import React from "react";
import { Link } from "react-router-dom";
import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { formatPrice } from "../../utils/format";

const FALLBACK = "https://images.unsplash.com/photo-1590736969955-71cc94901144?w=200&q=80";

function CartItem({ item }) {
  const { removeItem, updateQuantity } = useCart();
  return (
    <div className="flex gap-4 py-5 border-b border-gray-100 last:border-0">
      <div className="w-[70px] h-[80px] rounded-sm overflow-hidden bg-brand-cream shrink-0">
        <img
          src={item.image_url || FALLBACK}
          alt={item.name}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <h4 className="font-display text-base text-brand-charcoal truncate leading-tight">{item.name}</h4>
          <button onClick={() => removeItem(item.id)} className="text-gray-300 hover:text-brand-red transition-colors shrink-0 mt-0.5" aria-label={`Remove ${item.name}`}>
            <Trash2 size={14} />
          </button>
        </div>
        {item.category && <p className="font-body text-[11px] text-gray-400 mt-0.5">{item.category}</p>}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center border border-gray-200 rounded-sm">
            <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="px-2.5 py-1.5 text-gray-400 hover:text-brand-red transition-colors" aria-label="Decrease">
              <Minus size={11} />
            </button>
            <span className="px-2 font-body text-sm text-brand-charcoal w-6 text-center select-none">{item.quantity}</span>
            <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="px-2.5 py-1.5 text-gray-400 hover:text-brand-red transition-colors" aria-label="Increase">
              <Plus size={11} />
            </button>
          </div>
          <span className="font-body font-semibold text-sm text-brand-charcoal">{formatPrice(item.price * item.quantity)}</span>
        </div>
      </div>
    </div>
  );
}

export default function CartSidebar() {
  const { items, isOpen, closeCart, totalAmount, clearCart, totalItems } = useCart();

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-all duration-300 ${isOpen ? "visible opacity-100" : "invisible opacity-0"}`}
        onClick={closeCart}
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        aria-label="Shopping cart"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <ShoppingBag size={19} className="text-brand-red" />
            <h2 className="font-display text-xl text-brand-dark">Your Cart</h2>
            {items.length > 0 && (
              <span className="font-body text-xs text-gray-400">({totalItems} {totalItems === 1 ? "item" : "items"})</span>
            )}
          </div>
          <button onClick={closeCart} className="p-2 text-gray-400 hover:text-brand-red transition-colors" aria-label="Close cart">
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <ShoppingBag size={52} className="text-gray-200 mb-5" />
              <p className="font-display text-2xl text-gray-400 mb-2">Cart is empty</p>
              <p className="font-body text-sm text-gray-300 mb-8 max-w-[200px]">Explore our luxury fragrance collection</p>
              <button onClick={closeCart} className="btn-primary text-xs px-5 py-2.5">Continue Shopping</button>
            </div>
          ) : (
            <div className="py-2">
              {items.map((item) => <CartItem key={item.id} item={item} />)}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-6 border-t border-gray-100 bg-gray-50 shrink-0">
            <div className="flex justify-between items-center mb-1">
              <span className="font-body text-sm text-gray-500">Subtotal</span>
              <span className="font-body font-semibold text-brand-charcoal">{formatPrice(totalAmount)}</span>
            </div>
            <p className="font-body text-[11px] text-gray-400 mb-5">Shipping calculated at checkout.</p>
            <Link
              to="/checkout"
              onClick={closeCart}
              className="group w-full flex items-center justify-center gap-2 py-4 bg-brand-red text-white font-body font-medium text-sm tracking-wide hover:bg-red-700 transition-colors duration-200 rounded-sm mb-3"
            >
              Proceed to Checkout
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <button
              onClick={clearCart}
              className="w-full py-2.5 border border-gray-200 text-gray-400 font-body text-xs hover:border-gray-300 hover:text-gray-600 transition-colors rounded-sm"
            >
              Clear Cart
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
